import * as React from 'react';
import { Box, Tooltip, SxProps, Theme, CircularProgress } from '@mui/material';

export interface ActionButtonProps {
  /**
   * Icona da mostrare nel pulsante
   */
  icon: React.ReactNode;

  /**
   * Tooltip da mostrare al hover
   */
  tooltip?: string;

  /**
   * Funzione chiamata al click
   */
  onClick?: () => void;

  /**
   * Se il pulsante è disabilitato
   */
  disabled?: boolean;

  /**
   * Se il pulsante è in stato di caricamento
   */
  loading?: boolean;

  /**
   * Colore del pulsante quando è abilitato
   * @default 'primary.main'
   */
  color?: string;

  /**
   * Colore del pulsante al hover quando è abilitato
   * @default 'primary.dark'
   */
  hoverColor?: string;

  /**
   * Colore del testo/icona
   * @default 'white'
   */
  textColor?: string;

  /**
   * Props sx aggiuntive per personalizzare lo stile
   */
  sx?: SxProps<Theme>;

  /**
   * Variante del pulsante
   * @default 'filled'
   */
  variant?: 'filled' | 'outlined' | 'text';

  buttonType?: 'submit' | 'reset' | 'button' | undefined;
}

/**
 * Pulsante generico per azioni con icona, tooltip e gestione stati
 *
 * @example
 * ```tsx
 * <ActionButton
 *   icon={<SaveIcon />}
 *   tooltip="Salva modifiche"
 *   onClick={handleSave}
 *   disabled={!hasChanges}
 *   loading={isSaving}
 * />
 * ```
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  tooltip,
  onClick,
  disabled = false,
  loading = false,
  color = 'primary.main',
  hoverColor = 'primary.dark',
  textColor = 'white',
  sx = {},
  variant = 'filled',
  buttonType = 'button',
}) => {
  const isDisabled = disabled || loading;

  const getBackgroundColor = () => {
    if (variant === 'text') return 'transparent';
    if (variant === 'outlined') return 'transparent';
    return color;
  };

  const getBorder = () => {
    if (variant === 'outlined') return `1px solid`;
    return 'none';
  };

  const getTextColor = () => {
    if (variant === 'outlined' || variant === 'text') return color;
    return textColor;
  };

  const button = (
    <Box
      component="button"
      onClick={onClick}
      disabled={isDisabled}
      type={buttonType}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        backgroundColor: getBackgroundColor(),
        color: getTextColor(),
        border: getBorder(),
        borderColor: color,
        borderRadius: 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          backgroundColor: !isDisabled
            ? variant === 'filled'
              ? hoverColor
              : variant === 'outlined'
                ? `${color}22`
                : `${color}11`
            : undefined,
        },
        '&:disabled': {
          backgroundColor: variant === 'filled' ? 'action.disabledBackground' : 'transparent',
          color: 'action.disabled',
          borderColor: 'action.disabled',
        },
        ...sx,
      }}
    >
      {loading ? <CircularProgress size="1.5rem" /> : icon}
    </Box>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }

  return button;
};
